import { ProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { makeScheduleAsActive } from "services/schedule-versions-services";
import { message } from "antd";
import { TActivityHookInfo, TFeatureActivityWithInstance } from "../models";
import {
  FEATURE_ACTIVITIES,
  QUERY_SCHEDULE_VERSIONS
} from "../queries/queries";

type TScheduleVersion = {
  id: number;
  import_log_id: string;
  is_active: boolean;
  schedule_name: string;
  schedule_date: string;
  schedule_type: string;
  activated_at: null;
  created_at: string;
  import_log: {
    id: string;
    resolved_values_s3_prefix: string;
    source_values_s3_prefix: string;
    created_at: string;
    created_by_user: {
      id: string;
      first_name: string;
      last_name: string;
    };
  };
};

const getScheduleFileExtension = (
  selectedSchedule: TScheduleVersion,
  fileExtention: string
) => {
  if (!selectedSchedule?.import_log?.source_values_s3_prefix) return false;
  const fileExt = selectedSchedule?.import_log?.source_values_s3_prefix
    .split(".")
    .pop();
  if (fileExtention === fileExt) return true;
  return false;
};

export default function useActivityListHook() {
  const [loading, setLoading] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string>();
  const [selectedScheduleInfo, setSelectedScheduleInfo] =
    useState<TScheduleVersion>();
  const [hasActive, setHasActive] = useState(true);

  const { gqlClientForProject, tokenRetrievalState } =
    useContext(ProjectContext);

  const { data: featureActivitiesList, refetch: refetchActivityList } =
    useCIQQuery<{
      feature_activities: TFeatureActivityWithInstance[];
    }>(FEATURE_ACTIVITIES, {
      client: gqlClientForProject,
      skip: !gqlClientForProject
    });

  const { data: scheduleVersions, refetch: refetchScheduleVersions } =
    useCIQQuery<{
      schedule_import_details: TScheduleVersion[];
    }>(QUERY_SCHEDULE_VERSIONS, {
      client: gqlClientForProject,
      skip: !gqlClientForProject
    });

  const isCSVFile = useMemo(() => {
    return selectedScheduleInfo
      ? getScheduleFileExtension(selectedScheduleInfo, "csv")
      : false;
  }, [selectedScheduleInfo]);

  const createActiveSchedule = useCallback(
    (schedule_Id: string) => {
      setLoading(true);
      makeScheduleAsActive(schedule_Id, tokenRetrievalState?.token)
        .then((res: any) => {
          if (res.error) {
            message.error(res.error?.message || "Something went wrong");
          } else {
            refetchActivityList();
            refetchScheduleVersions();
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [refetchActivityList, refetchScheduleVersions, tokenRetrievalState?.token]
  );

  useEffect(() => {
    if (featureActivitiesList && featureActivitiesList.feature_activities) {
      if (featureActivitiesList.feature_activities.length) {
        setSelectedScheduleId(
          featureActivitiesList.feature_activities[0].import_log_id
        );
      } else if (scheduleVersions?.schedule_import_details?.length) {
        setSelectedScheduleId(
          scheduleVersions?.schedule_import_details[0].import_log_id
        );
        createActiveSchedule(
          scheduleVersions?.schedule_import_details[0].import_log_id
        );
      }
    }
  }, [
    createActiveSchedule,
    featureActivitiesList,
    scheduleVersions?.schedule_import_details
  ]);

  useEffect(() => {
    if (scheduleVersions && scheduleVersions?.schedule_import_details?.length) {
      const hasActiveTemp = scheduleVersions?.schedule_import_details.some(
        (e) => e.is_active
      );
      setHasActive(hasActiveTemp);
      const selectedScheduleObj =
        scheduleVersions?.schedule_import_details?.find(
          (e: any) => e?.import_log?.id === selectedScheduleId
        );
      setSelectedScheduleInfo(selectedScheduleObj);
    }
  }, [scheduleVersions, selectedScheduleId]);

  return {
    activities: featureActivitiesList?.feature_activities || [],
    isCSVFile,
    hasActive,
    loading,
    createActiveSchedule,
    selectedScheduleId,
    refetchScheduleVersions,
    scheduleVersionsOptions:
      scheduleVersions?.schedule_import_details?.map((x) => ({
        value: x.import_log_id,
        label: `${x?.schedule_name}_${x?.schedule_date}`
      })) || []
  } as TActivityHookInfo;
}
